import { X } from "lucide-react";
import React from "react";
import { useModalFocusTrap } from "./useModalFocusTrap";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /**
   * Body copy. Widened from `string` to `ReactNode` for PWA-2546, whose
   * unpublish/delete warnings need several paragraphs and an inline slug.
   * Backward compatible -- `ReactNode` accepts `string`, so existing callers
   * are unaffected.
   */
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  isDestructive?: boolean;
  loading?: boolean;
  confirmDisabled?: boolean;
  /**
   * Renders a single dismiss button with no confirm path, for blocking errors
   * the user cannot action (PWA-2597's unaddable-stock dialog). `onConfirm` is
   * not called in this mode. Defaults off, so existing callers are unaffected.
   */
  dismissOnly?: boolean;
  dismissText?: string;
  appearance?: "default" | "alpha";
  /** Confirm-button label while `loading` (PWA-2748 dialogs use "Scheduling...", "Sending..."). */
  loadingText?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  isDestructive = false,
  loading = false,
  confirmDisabled = false,
  dismissOnly = false,
  dismissText = "Close",
  appearance = "default",
  loadingText = "Processing...",
}) => {
  const isAlpha = appearance === "alpha";
  const titleId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement>(null);
  useModalFocusTrap({ closeDisabled: loading, dialogRef, isOpen, onClose });

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isAlpha ? "bg-v7-ink/60" : "bg-black/60"}`}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-appearance={appearance}
        className={`w-full max-w-md overflow-hidden border shadow-2xl ${isAlpha ? "rounded-v7-popover border-v7-ink/15 bg-v7-white font-v7-sans" : "rounded-xl border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface-raised"}`}
      >
        <div className={`flex items-center justify-between border-b px-5 py-4 ${isAlpha ? "border-v7-ink/15" : "border-gray-200 dark:border-rp-separator"}`}>
          <h2 id={titleId} className={`text-base font-bold ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            aria-label="Close confirmation"
            className={isAlpha
              ? "rounded-v7-control p-1 text-v7-ink/45 transition-colors hover:bg-v7-paper hover:text-v7-ink"
              : "rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-200"}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4">
          <div className={`text-sm ${isAlpha ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-300"}`}>
            {message}
          </div>
        </div>

        <div className={`flex items-center justify-end gap-2 border-t px-5 py-3 ${isAlpha ? "border-v7-ink/15" : "border-gray-200 dark:border-rp-separator"}`}>
          {dismissOnly ? (
            <button
              type="button"
              onClick={onClose}
              className={isAlpha
                ? "rounded-v7-control bg-v7-ink px-4 py-2 text-sm font-semibold text-v7-white transition-colors hover:bg-v7-ink-soft"
                : "rounded-lg bg-rp-amber px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600"}
            >
              {dismissText}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className={isAlpha
                  ? "rounded-v7-control border border-v7-ink/30 bg-v7-white px-4 py-2 text-sm font-semibold text-v7-ink transition-colors hover:border-v7-ink disabled:opacity-50"
                  : "rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"}
              >
                {cancelText}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={loading || confirmDisabled}
                className={`px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${isAlpha ? "rounded-v7-control text-v7-white" : "rounded-lg text-white"} ${
                  isDestructive
                    ? isAlpha ? "bg-v7-rust hover:bg-v7-rust/90" : "bg-red-600 hover:bg-red-700"
                    : isAlpha ? "bg-v7-ink hover:bg-v7-ink-soft" : "bg-rp-amber hover:bg-yellow-600"
                }`}
              >
                {loading ? loadingText : confirmText}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
